// Organize mode recount (api-contract.md, lead backend): an EXPLICIT count
// session in which each scanned card is an observation with a stable id (the
// capture token), so replays never double count and a deliberate "another
// copy" is a new observation. Counts are absolute per finish-specific
// physical-kind group and are only ever announced from a server receipt.
// Nothing here writes; these are pure helpers used by the provider/scanner.

export const CONFLICTS = ['COUNT_SCOPE_REQUIRED', 'COUNT_COMMITTED', 'COUNT_METADATA_CONFLICT', 'RECOUNT_CONFLICT'];

// Observation id for a capture: the capture token itself (stable across
// retries); a deliberate second copy of the same card gets its own id.
export function observationId(token, copy = 0) { return copy > 0 ? `${token}:copy${copy}` : String(token); }

// Whether a recognised card should be recorded as an observation. Only inside
// an explicit active session (never on a generic owned lookup), and never for
// a card that is merely lingering in frame.
export function shouldObserve({ mode, session, lingering = false }) {
  return mode === 'organize' && !!session && session.status === 'active' && !lingering;
}

// The scanner context binding for a recount observation (verified server-side).
export function recountBound(rec) {
  if (!rec || !rec.recount) return null;
  return { kind: 'recount', token: rec.recount.observation_id, session_id: rec.recount.session_id, card_id: rec.card_id, printing: rec.recount.printing || rec.printing || null };
}

// A pending adjust is retained COMPLETE across a scope clarification: the
// caller only adds scope_entry_id and retries; card, finish, quantity and the
// observation id never change.
export function retainPending(op, conflict) {
  if (!op) return null;
  const candidates = Array.isArray(conflict?.candidates) ? conflict.candidates : [];
  return { ...op, candidates, code: conflict?.code || 'COUNT_SCOPE_REQUIRED', error: conflict?.error || null };
}
export function withScope(pending, entry_id) {
  if (!pending || entry_id == null) return null;
  const { candidates, code, error, ...op } = pending; // eslint-disable-line no-unused-vars
  return { ...op, scope_entry_id: Number(entry_id) };
}

// Bucket presentation: "Judge #076 · Reverse Holofoil: 1" — `count` is the
// surveyed count the server applied for that finish-specific kind (all
// locations by default); unsurveyed kinds are never touched.
export function bucketLine(b, t = (k, v) => `${k} ${JSON.stringify(v || {})}`) {
  if (!b) return '';
  const name = [b.name, b.number ? `#${b.number}` : null].filter(Boolean).join(' ');
  return t('scan.recount.bucket', { name, printing: b.printing || '', count: Number(b.count ?? b.target ?? 0) });
}

export function sessionSummary(session) {
  const buckets = Array.isArray(session?.buckets) ? session.buckets : [];
  const observations = Array.isArray(session?.observations) ? session.observations : [];
  return { observations: observations.length, buckets: buckets.length, counted: buckets.reduce((n, b) => n + Number(b.count ?? b.target ?? 0), 0), surveyed: buckets.filter(b => b.surveyed !== false).length };
}

export function conflictKey(code) { return CONFLICTS.includes(code) ? `scan.recount.conflict.${code}` : 'scan.recount.conflict.OTHER'; }
